import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { T } from "@/lib/theme";
import { ArrowUpRight } from "lucide-react";
import { OrbitCloseDispatch } from "@/pages/OrbitClosePreview";

/**
 * The Brand — keeps the approved opening composition (E on top, RENOWN below,
 * left-aligned, persistent — same letter-rise animation as the intro reveal).
 * Below the hero are the structured brand-story sections (final copy).
 * Closes with The Erenown Register (no orbital animation above the footer).
 */
export default function Brand() {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShown(true), 80);
    return () => clearTimeout(t);
  }, []);

  const T2_BG = "#0A0A0B";
  const T2_TEXT = "#E8E1D2";
  const T2_MUTED = "#7A756A";

  const font = {
    fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
    fontWeight: 800,
    textTransform: "uppercase",
    color: T2_TEXT,
    letterSpacing: "-0.06em",
    lineHeight: 0.9,
    fontSize: "clamp(120px, 18vw, 280px)",
  };

  return (
    <div data-testid="brand-page">
      {/* HERO — persistent E/RENOWN composition (same as intro reveal, does not disappear) */}
      <section
        data-testid="brand-hero"
        className="relative overflow-hidden"
        style={{ minHeight: "100vh", background: T2_BG }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            paddingLeft: "clamp(24px, 6vw, 96px)",
          }}
        >
          <div data-testid="brand-hero-e" style={font}>
            <span style={{ display: "inline-block", color: T.warm, animation: "letter-rise 0.7s cubic-bezier(.2,.9,.2,1) 0s both" }}>E</span>
          </div>
          <div data-testid="brand-hero-renown" style={{ ...font, marginTop: "-0.04em" }}>
            <span style={{ display: "inline-block", animation: "letter-rise 0.7s cubic-bezier(.2,.9,.2,1) 0.25s both" }}>R</span>
            <span style={{ display: "inline-block", animation: "letter-rise 0.7s cubic-bezier(.2,.9,.2,1) 0.32s both" }}>E</span>
            <span style={{ display: "inline-block", animation: "letter-rise 0.7s cubic-bezier(.2,.9,.2,1) 0.39s both" }}>N</span>
            <span style={{ display: "inline-block", animation: "letter-rise 0.7s cubic-bezier(.2,.9,.2,1) 0.46s both" }}>O</span>
            <span style={{ display: "inline-block", animation: "letter-rise 0.7s cubic-bezier(.2,.9,.2,1) 0.53s both" }}>W</span>
            <span style={{ display: "inline-block", animation: "letter-rise 0.7s cubic-bezier(.2,.9,.2,1) 0.60s both" }}>N</span>
          </div>
        </div>

        {/* Top meta strip */}
        <div className="absolute top-0 left-0 right-0 z-10">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-8 md:pt-12 flex items-center justify-between">
            <span className="font-plexmono text-[10.5px] tracking-[0.28em] uppercase" style={{ color: T.accent }}>
              THE BRAND
            </span>
            <span className="font-plexmono text-[10.5px] tracking-[0.28em] uppercase" style={{ color: T2_MUTED }}>
              ERENOWN · CENTRAL STRUCTURE
            </span>
          </div>
        </div>

        {/* Bottom strip — subheadline + supporting CTA */}
        <div
          className="absolute bottom-0 left-0 right-0 z-10"
          style={{
            opacity: shown ? 1 : 0,
            transform: shown ? "translateY(0)" : "translateY(12px)",
            transition: "opacity 1s ease-out 1.4s, transform 1s ease-out 1.4s",
          }}
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-10 md:pb-14 flex flex-wrap items-end justify-between gap-6">
            <p
              className="max-w-xl font-intertight text-[15px] leading-[1.85]"
              style={{ color: T2_TEXT, opacity: 0.78 }}
            >
              A name shaped by the digital era — and by the belief that reputation is earned through structure,
              delivery, and lasting value.
            </p>
            <div className="flex items-center gap-4">
              <Link
                to="/orbit"
                data-testid="brand-cta-orbit"
                className="group inline-flex items-center gap-3 px-7 py-4 font-plexmono text-[11px] tracking-[0.28em] uppercase"
                style={{ background: T.warm, color: T2_BG }}
              >
                Enter the Orbit <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/capabilities"
                data-testid="brand-cta-capabilities"
                className="inline-flex items-center gap-3 px-7 py-4 border font-plexmono text-[11px] tracking-[0.28em] uppercase"
                style={{ borderColor: `${T2_TEXT}33`, color: T2_TEXT }}
              >
                Capabilities
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STORY SECTIONS — final copy */}
      {[
        {
          num: "I",
          label: "Why Erenown",
          title: <>Why <span className="italic" style={{ color: T.accent }}>Erenown</span>.</>,
          body: [
            "The name Erenown reflects a digital-age context and a long-term respect for structured work. It points to a world that is increasingly electronic, connected, and interdependent, while also reflecting the kind of regard that follows useful, disciplined, and enduring effort.",
            "Erenown is not about visibility for its own sake. It is about building things that hold together well enough to matter over time.",
          ],
        },
        {
          num: "II",
          label: "A center, not a fragment",
          title: <>A center, <span className="italic" style={{ color: T.warm }}>not a fragment</span>.</>,
          body: [
            "Erenown operates as the central structure behind a growing orbit of brands and solution lines. Rather than presenting disconnected offers, it provides a steady center through which new initiatives, operational models, and structured opportunities can be positioned, aligned, and expanded.",
            "This role allows Erenown to remain stable at the center while enabling specialized lines to develop their own logic, language, and purpose.",
          ],
        },
        {
          num: "III",
          label: "Capability with context",
          title: <>Built to connect <span className="italic" style={{ color: T.accent }}>capability</span> with context.</>,
          body: [
            "Erenown combines business enablement, institutional thinking, cross-border awareness, and a strong respect for structure. Some opportunities begin with practical needs. Others grow into broader operational, institutional, or strategic pathways.",
            "The strength of the model lies in continuity: entry, operation, expansion, and evolution are treated as connected stages rather than isolated events.",
          ],
        },
        {
          num: "IV",
          label: "Recognition follows substance",
          title: <>Recognition should follow <span className="italic" style={{ color: T.accent }}>substance</span>.</>,
          body: [
            "Erenown is not built around volume, trend language, or the pursuit of attention. It is built around a more durable principle: if the work is useful, structured, and credible, reputation becomes a consequence rather than an objective.",
          ],
        },
      ].map((s, i) => (
        <section
          key={s.num}
          className="border-t"
          style={{ borderColor: T.border, background: i % 2 === 0 ? T.surface : T.bg }}
          data-testid={`brand-story-${s.num}`}
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-12 gap-10">
            <div className="col-span-12 md:col-span-4">
              <div className="flex items-center gap-4 font-plexmono text-[10.5px] tracking-[0.22em] uppercase" style={{ color: T.muted }}>
                <span>{s.num}</span>
                <span className="flex-1 h-px" style={{ background: T.border }} />
                <span style={{ color: T.accent }}>{s.label}</span>
              </div>
            </div>
            <div className="col-span-12 md:col-span-8">
              <h2 className="font-fraunces font-light text-4xl md:text-6xl leading-[1.08]" style={{ color: T.text, fontVariationSettings: "'SOFT' 50" }}>
                {s.title}
              </h2>
              <div className="mt-8 max-w-2xl space-y-6 font-intertight text-[15.5px] leading-[1.9]" style={{ color: T.textSoft }}>
                {s.body.map((p, k) => (
                  <p key={k}>{p}</p>
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CLOSING — The center holds */}
      <section className="border-t relative overflow-hidden" style={{ borderColor: T.border, background: T.surface }} data-testid="brand-cta">
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="font-plexmono text-[10.5px] tracking-[0.28em] uppercase" style={{ color: T.warm }}>
              V · The center holds
            </div>
            <h2 className="font-fraunces font-light text-4xl md:text-6xl mt-8 leading-[1.05]" style={{ color: T.text, fontVariationSettings: "'SOFT' 60" }}>
              The center <span className="italic" style={{ color: T.accent }}>holds</span>.
            </h2>
            <p className="mt-8 max-w-2xl font-intertight text-[15.5px] leading-[1.9]" style={{ color: T.textSoft }}>
              At the center of the orbit, Erenown remains the structure that connects lines, supports growth,
              and gives long-term direction to a wider family of brands and systems.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <Link to="/orbit" data-testid="brand-cta-button"
              className="group inline-flex items-center gap-3 px-8 py-4 font-plexmono text-[11px] tracking-[0.3em] uppercase"
              style={{ background: T.warm, color: T.bg }}>
              Enter the Orbit <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </section>

      <OrbitCloseDispatch />
    </div>
  );
}
